import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Download, Search, Users, UserCircle } from 'lucide-react';
import { apiService } from '../services/api';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import './EventHome.css';
import './EventConnections.css';

const EventConnections = () => {
    const { eventNameId } = useParams();
    const [event, setEvent] = useState(null);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    useEffect(() => {
        if (event?.EventId && event?.ic_event_id) {
            fetchConnections();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?.EventId, event?.ic_event_id]);

    const fetchEventData = async () => {
        try {
            setLoading(true);
            const eventResponse = await apiService.getEventByNameId(eventNameId);
            setEvent(eventResponse.data);
        } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event');
            setLoading(false);
        }
    };

    const fetchConnections = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await apiService.getConnections(event.EventId, event.ic_event_id);
            if (response.data?.success) {
                setConnections(response.data.connections || []);
            } else {
                setError(response.data?.error || 'Failed to load connections');
            }
        } catch (err) {
            console.error('Error fetching connections:', err);
            setError('Failed to load connections');
        } finally {
            setLoading(false);
        }
    };

    const filteredConnections = useMemo(() => {
        if (!searchTerm.trim()) return connections;
        const term = searchTerm.toLowerCase();
        return connections.filter(c =>
            (c.firstName && c.firstName.toLowerCase().includes(term)) ||
            (c.lastName && c.lastName.toLowerCase().includes(term)) ||
            (c.email && c.email.toLowerCase().includes(term)) ||
            (c.companyName && c.companyName.toLowerCase().includes(term))
        );
    }, [connections, searchTerm]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        });
    };

    const exportToCsv = () => {
        if (filteredConnections.length === 0) return;

        const headers = ['First Name', 'Last Name', 'Email', 'Company', 'Mobile Phone', 'Date Connected'];
        const rows = filteredConnections.map(c => [
            c.firstName || '',
            c.lastName || '',
            c.email || '',
            c.companyName || '',
            c.mobilePhone || '',
            c.dateCreated ? formatDate(c.dateCreated) : ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `connections_${eventNameId}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading && !event) {
        return (
            <>
                <NavigationBar eventNameId={eventNameId} />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="connections-page">
                            <LoadingSpinner message="Loading..." />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavigationBar eventNameId={eventNameId} />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar">
                    <div className="connections-page">
                        <div className="connections-header">
                            <div className="connections-header-top">
                                <div>
                                    <h1>Connections</h1>
                                    <p className="connections-subtitle">
                                        Your scanned connections at {event?.Name || eventNameId}
                                    </p>
                                </div>
                                {connections.length > 0 && (
                                    <button className="export-btn" onClick={exportToCsv}>
                                        <Download size={16} />
                                        <span>Export CSV</span>
                                    </button>
                                )}
                            </div>

                            {connections.length > 0 && (
                                <div className="connections-toolbar">
                                    <div className="connections-search">
                                        <Search size={16} />
                                        <input
                                            type="text"
                                            placeholder="Search by name, email, or company..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <span className="connections-count">
                                        {filteredConnections.length} connection{filteredConnections.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="connections-content">
                            {loading ? (
                                <div className="connections-loading">
                                    <LoadingSpinner message="Loading connections..." />
                                </div>
                            ) : error ? (
                                <div className="connections-error">
                                    <AlertCircle size={48} />
                                    <h3>Unable to Load Connections</h3>
                                    <p>{error}</p>
                                    <button onClick={fetchConnections} className="retry-btn">
                                        Try Again
                                    </button>
                                </div>
                            ) : filteredConnections.length === 0 ? (
                                <div className="connections-empty">
                                    <Users size={48} />
                                    <h3>{searchTerm ? 'No Matching Connections' : 'No Connections Yet'}</h3>
                                    <p>
                                        {searchTerm
                                            ? 'Try adjusting your search terms.'
                                            : 'Scan attendee badges to start building your connections.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="connections-table-container">
                                    <table className="connections-table">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Company</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Date Connected</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredConnections.map((connection, index) => (
                                                <tr key={`${connection.toContactId}-${index}`}>
                                                    <td className="name-cell">
                                                        <div className="connection-name-row">
                                                            {connection.imageUrl ? (
                                                                <img
                                                                    src={connection.imageUrl}
                                                                    alt=""
                                                                    className="connection-avatar"
                                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                />
                                                            ) : null}
                                                            <span
                                                                className="connection-avatar-fallback"
                                                                style={connection.imageUrl ? { display: 'none' } : {}}
                                                            >
                                                                <UserCircle size={32} />
                                                            </span>
                                                            <span className="connection-full-name">
                                                                {connection.firstName} {connection.lastName}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="company-cell">{connection.companyName || '—'}</td>
                                                    <td className="email-cell">
                                                        {connection.email ? (
                                                            <a href={`mailto:${connection.email}`}>{connection.email}</a>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="phone-cell">{connection.mobilePhone || <span className="not-sharing">Not Shared</span>}</td>
                                                    <td className="date-cell">{formatDate(connection.dateCreated)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EventConnections;
